import { Box, Button, TextField, Typography, Fade, InputAdornment, IconButton } from "@mui/material";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useEffect, useState, useRef, useCallback } from "react";

interface PeriodDatePickerProps {
  startDate: Date;
  endDate: Date;
  onChange: (start: Date, end: Date) => void;
  onApply?: (start: Date, end: Date) => void;
}

// Format: JJ-MM-AAAA
function formatDateFR(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// Parse: JJ-MM-AAAA ou AAAA-MM-JJ
function parseDateFR(val: string): Date {
  // Si format JJ-MM-AAAA
  if (val.match(/^\d{2}-\d{2}-\d{4}$/)) {
    const [day, month, year] = val.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
  }
  // Si format AAAA-MM-JJ (fallback)
  if (val.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(val + "T00:00:00");
  }
  // Fallback: essayer de parser comme Date
  const parsed = new Date(val);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

function parseInputDate(val: string) {
  return parseDateFR(val);
}

const PeriodDatePicker = ({
  startDate,
  endDate,
  onChange,
  onApply,
}: PeriodDatePickerProps) => {
  const [localStart, setLocalStart] = useState(formatDateFR(startDate));
  const [localEnd, setLocalEnd] = useState(formatDateFR(endDate));
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [endError, setEndError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const autoFormatDate = (value: string, previousValue: string): string => {
    if (value.length < previousValue.length) {
      return value;
    }

    const cleaned = value.replace(/[^\d-]/g, '');
    
    if (cleaned.includes('-')) {
      return cleaned;
    }

    const numbers = cleaned.replace(/-/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}-${numbers.slice(2, 4)}-${numbers.slice(4, 8)}`;
    }
  };

  useEffect(() => {
    setLocalStart(formatDateFR(startDate));
  }, [startDate]);
  useEffect(() => {
    setLocalEnd(formatDateFR(endDate));
  }, [endDate]);

  useEffect(() => {
    if (localStart && !localStart.match(/^\d{2}-\d{2}-\d{4}$/)) {
      setStartError("Format attendu: JJ-MM-AAAA");
    } else {
      setStartError(null);
    }

    if (localEnd && !localEnd.match(/^\d{2}-\d{2}-\d{4}$/)) {
      setEndError("Format attendu: JJ-MM-AAAA");
    } else {
      setEndError(null);
    }

    if (localStart.match(/^\d{2}-\d{2}-\d{4}$/) && localEnd.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const s = parseInputDate(localStart);
      const e = parseInputDate(localEnd);
      if (e < s) {
        setError("La date de fin ne peut pas être antérieure à la date de début.");
      } else {
        setError(null);
      }
    } else {
      setError(null);
    }
  }, [localStart, localEnd]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleApply = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const s = parseInputDate(localStart);
    const e = parseInputDate(localEnd);
    if (e >= s) {
      if (onApply) {
        onApply(s, e);
      } else {
        onChange(s, e);
      }
    }
  }, [localStart, localEnd, onChange, onApply]);

  const handleDateChange = useCallback((value: string, isStart: boolean) => {
    const previousValue = isStart ? localStart : localEnd;
    const formatted = autoFormatDate(value, previousValue);
    
    if (isStart) {
      setLocalStart(formatted);
    } else {
      setLocalEnd(formatted);
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const startValue = isStart ? formatted : localStart;
      const endValue = isStart ? localEnd : formatted;
      
      if (startValue.match(/^\d{2}-\d{2}-\d{4}$/) && endValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const s = parseInputDate(startValue);
        const e = parseInputDate(endValue);
        if (e >= s) {
          onChange(s, e);
        }
      }
    }, 3000);
  }, [localStart, localEnd, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApply();
    }
  };

  const handlePreset = (type: "today" | "month" | "year" | number) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let start = new Date(today);
    let end = new Date(today);

    if (type === "today") {
    } else if (type === "month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 0, 0, 0, 0);
    } else if (type === "year") {
      start = new Date(today.getFullYear(), 0, 1, 0, 0, 0, 0);
      end = new Date(today.getFullYear(), 11, 31, 0, 0, 0, 0);
    } else if (typeof type === "number") {
      start = new Date(today);
      end = new Date(today);
      end.setDate(end.getDate() + type - 1);
    }

    setLocalStart(formatDateFR(start));
    setLocalEnd(formatDateFR(end));
    if (onApply) {
      onApply(start, end);
    } else {
      onChange(start, end);
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        Sélection d'une plage de dates
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={1}>
        <Button size="small" variant="outlined" onClick={() => handlePreset("today")}>
          Aujourd’hui
        </Button>
        <Button size="small" variant="outlined" onClick={() => handlePreset("month")}>
          Mois en cours
        </Button>
        <Button size="small" variant="outlined" onClick={() => handlePreset("year")}>
          Année en cours
        </Button>
        <Button size="small" variant="outlined" onClick={() => handlePreset(7)}>
          7 jours
        </Button>
        <Button size="small" variant="outlined" onClick={() => handlePreset(14)}>
          14 jours
        </Button>
        <Button size="small" variant="outlined" onClick={() => handlePreset(30)}>
          30 jours
        </Button>
      </Box>

      <Box display="flex" flexDirection="column" alignItems="center" mt={1}>
        <Box display="flex" gap={3} alignItems="flex-start">
          <Box position="relative">
            <TextField
              type="text"
              size="small"
              value={localStart}
              onChange={(e) => handleDateChange(e.target.value, true)}
              onBlur={handleApply}
              onKeyDown={handleKeyDown}
              label="Date de début"
              placeholder="JJ-MM-AAAA"
              InputLabelProps={{ shrink: true }}
              error={!!startError}
              helperText={startError || "Format: JJ-MM-AAAA"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const input = document.getElementById('hidden-date-start') as HTMLInputElement;
                        input?.showPicker();
                      }}
                      sx={{ padding: 0 }}
                    >
                      <CalendarTodayIcon fontSize="small" color="action" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <input
              id="hidden-date-start"
              type="date"
              style={{
                position: 'absolute',
                opacity: 0,
                pointerEvents: 'none',
                width: 0,
                height: 0,
              }}
              value={localStart.match(/^\d{2}-\d{2}-\d{4}$/) ? localStart.split('-').reverse().join('-') : ''}
              onChange={(e) => {
                if (e.target.value) {
                  const [year, month, day] = e.target.value.split('-');
                  const formatted = `${day}-${month}-${year}`;
                  handleDateChange(formatted, true);
                }
              }}
            />
          </Box>
          <Box position="relative">
            <TextField
              type="text"
              size="small"
              value={localEnd}
              onChange={(e) => handleDateChange(e.target.value, false)}
              onBlur={handleApply}
              onKeyDown={handleKeyDown}
              label="Date de fin"
              placeholder="JJ-MM-AAAA"
              InputLabelProps={{ shrink: true }}
              error={!!endError}
              helperText={endError || "Format: JJ-MM-AAAA"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const input = document.getElementById('hidden-date-end') as HTMLInputElement;
                        input?.showPicker();
                      }}
                      sx={{ padding: 0 }}
                    >
                      <CalendarTodayIcon fontSize="small" color="action" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <input
              id="hidden-date-end"
              type="date"
              style={{
                position: 'absolute',
                opacity: 0,
                pointerEvents: 'none',
                width: 0,
                height: 0,
              }}
              value={localEnd.match(/^\d{2}-\d{2}-\d{4}$/) ? localEnd.split('-').reverse().join('-') : ''}
              onChange={(e) => {
                if (e.target.value) {
                  const [year, month, day] = e.target.value.split('-');
                  const formatted = `${day}-${month}-${year}`;
                  handleDateChange(formatted, false);
                }
              }}
            />
          </Box>
        </Box>
        <Box height={24} mt={0.5} display="flex" alignItems="center">
          <Fade in={!!error} unmountOnExit>
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </Fade>
        </Box>
      </Box>
    </Box>
  );
};

export default PeriodDatePicker;
